// frontend/src/pages/LoginPage.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-02
// Updated:      2025-06-30
//
// Description:  Secure login page using unified anpr_token and useAuth context.

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext.jsx";
import { useAuth } from "../context/AuthContext";

const LoginPage = () => {
  const { theme } = useTheme();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    const success = await login(username, password);
    if (success) {
      navigate("/dashboard");
    } else {
      setError("Invalid credentials or server error");
    }
  };

  const getButtonColor = () => {
    if (active) return theme.button_action?.click || "#000";
    if (hover) return theme.button_action?.hover || "#000";
    return theme.button_action?.normal || "#000";
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--primary-color)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--font-body)",
        padding: "1rem",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "15px",
          padding: "2rem",
          width: "100%",
          maxWidth: "420px",
          boxShadow: "0 4px 14px rgba(0, 0, 0, 0.1)",
          textAlign: "center",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {theme.logo_login_url && (
          <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <img
              src={theme.logo_login_url}
              alt="Login Logo"
              style={{
                width: "100%",
                maxWidth: "350px",
                height: "auto",
                objectFit: "contain",
                marginBottom: "1.5rem",
              }}
            />
          </div>
        )}
        <h2
          style={{
            marginBottom: "2rem",
            fontFamily: "var(--font-header)",
            fontSize: "1.4rem",
          }}
        >
          {theme.client_name || "Vision"} ANPR Login
        </h2>
        <form
          onSubmit={handleLogin}
          style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}
        >
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            style={{
              width: "80%",
              padding: "12px",
              marginBottom: "1rem",
              border: "1px solid #ccc",
              borderRadius: "6px",
              fontSize: "1rem",
            }}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{
              width: "80%",
              padding: "12px",
              marginBottom: "1rem",
              border: "1px solid #ccc",
              borderRadius: "6px",
              fontSize: "1rem",
            }}
          />
          {error && <div style={{ color: "red", marginBottom: "1rem" }}>{error}</div>}
          <button
            type="submit"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => {
              setHover(false);
              setActive(false);
            }}
            onMouseDown={() => setActive(true)}
            onMouseUp={() => setActive(false)}
            style={{
              width: "60%",
              backgroundColor: getButtonColor(),
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              fontWeight: "bold",
              fontFamily: "var(--font-body)",
              cursor: "pointer",
              fontSize: "1rem",
              transition: "background-color 0.2s ease-in-out",
            }}
          >
            Login
          </button>
        </form>
        <p
          style={{
            marginTop: "2rem",
            fontSize: "0.75rem",
            color: "#888",
          }}
        >
          &copy; {new Date().getFullYear()} Castle Traffic Ltd. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
